import { bpsToPercentString } from '@/utils/bps';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { NumberField, Table } from '@heroui/react';
import { Widget } from '@heroui-pro/react';
import type { ChartAccount } from '@/api/models';
import { cn } from '@/utils/cnUtils';
import { formatGel } from '@/utils/money';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { findLedgerAccount } from '@/pages/accounting/books/chartOfAccounts/utils';
import { balanceMessage, type PreviewResult } from '../../operationPreview';

type PreviewPanelProps = {
    preview: PreviewResult;
    accounts: ChartAccount[];
    sampleGel: string;
    onSampleChange: (value: string) => void;
    /** The rate the preview applied, in basis points — stated, never assumed. */
    vatRateBps: number;
    isReadOnly: boolean;
};

/**
 * The sample amount renders as money, because it is money.
 *
 * Same construction as `manualImport`'s `LabeledNumberField` — a `NumberField`
 * with steppers and GEL formatting — rather than a bare text input. The field is
 * the whole preview's input, and stepping it is how an author checks that a
 * recipe still balances at 117.99 and 118.01, which is exactly where VAT
 * rounding goes wrong.
 *
 * `narrowSymbol` gives ₾, the sign every other figure on this screen uses. The
 * default renders the ISO code, which would make the one editable amount the
 * only one on the page not written the way the ledger writes money — and it
 * costs three characters of width the field does not have.
 */
const SAMPLE_FORMAT: Intl.NumberFormatOptions = {
    currency: 'GEL',
    currencyDisplay: 'narrowSymbol',
    style: 'currency',
};

/**
 * What the recipe would post, for one sample amount.
 *
 * This is the half of the editor that makes the other half legible: an
 * accountant reads a journal, not a form. Every line shows its account's code
 * and name, its figure in the right column, and the totals underneath prove the
 * entry balances.
 *
 * Built on the kit's `Widget` and `Table`: the `<section>` around it stays bare
 * and carries only the accessible name, so the surface treatment has exactly
 * one owner. `Table variant="secondary"` drops the table's own background and
 * lets `Widget.Content` provide the elevated surface, the pattern the kit
 * documents for tabular content inside a widget.
 *
 * Four decisions worth keeping:
 *
 * **The sample amount lives in the widget header, unlabelled.** It is the one
 * input on the screen that changes nothing about the recipe — it only changes
 * this table — so putting it anywhere else would imply it was part of what gets
 * saved. The visible label is dropped because the widget's own title already
 * says what the panel is and the ₾ says what the number is; the accessible name
 * is carried by `aria-label` so nothing is lost to a screen reader.
 *
 * **A slot renders as its label in a dashed box, not as a blank.** The whole
 * point of a slot is that the account is decided later; drawing it as an empty
 * cell would read as a line the author forgot to finish.
 *
 * **The empty side of a line is an em dash, not whitespace.** A debit line and a
 * credit line differ only by which column carries the figure, and a blank cell
 * reads as a missing value rather than as a deliberate nil.
 *
 * **The totals are a row in the body, not `Table.Footer`.** `Table.Footer` sits
 * outside the column grid — it is for pagination — and totals that did not line
 * up under the figures they add up would be worse than no totals. The footer
 * carries the balance verdict instead, which is a sentence rather than a sum.
 */
export const PreviewPanel: React.FC<PreviewPanelProps> = ({
    preview,
    accounts,
    sampleGel,
    onSampleChange,
    vatRateBps,
    isReadOnly,
}) => {
    const { t } = useTranslation('pages');
    const balance = balanceMessage(preview);

    return (
        <section
            aria-label={t('operations.editor.preview.title')}
            {...getNodeTestIdProp('operation-editor-preview')}
        >
            <Widget>
                <Widget.Header className="my-4">
                    <Widget.Title>
                        {t('operations.editor.preview.title')}
                    </Widget.Title>
                    <NumberField
                        aria-label={t('operations.editor.preview.sample')}
                        // The width lives here, on the flex item in the
                        // widget's header, not on the group inside it: a
                        // `max-w-full` on the group resolves against this
                        // element, which has no width of its own, so it clamps
                        // nothing. `min-w-0` is what actually lets the field
                        // give way when the header runs out of room — a flex
                        // item's default `min-width: auto` refuses to shrink
                        // below its content and would push the panel wider
                        // than its 43fr column.
                        className="w-72 min-w-0 max-w-full"
                        formatOptions={SAMPLE_FORMAT}
                        isDisabled={isReadOnly}
                        minValue={0}
                        onChange={(next) =>
                            onSampleChange(
                                next === undefined || Number.isNaN(next)
                                    ? ''
                                    : String(next),
                            )
                        }
                        value={sampleGel === '' ? undefined : Number(sampleGel)}
                        variant="secondary"
                        {...getNodeTestIdProp('operation-editor-sample-amount')}
                    >
                        {/* `w-72`, not `w-44`. The group is a grid of
                            `40px 1fr 40px`, so the two step buttons take 80px
                            before the input gets any: at 176px that left ~96px
                            for a value that needs about 130, and
                            `₾9,000,000.00` was clipped mid-figure.

                            The steppers stay, and not out of caution — they are
                            what the grid is built around, so removing them
                            drops the input into the first 40px column and clips
                            the value far worse than the narrow width did. They
                            also earn their place here specifically: stepping is
                            how an author checks that a recipe still balances at
                            117.99 and 118.01, which is exactly where VAT
                            rounding goes wrong.

                            The group fills whatever width the field is given —
                            the sizing and the shrinking are on the `NumberField`
                            above, for the reason noted there. */}
                        <NumberField.Group className="w-full">
                            <NumberField.DecrementButton />
                            <NumberField.Input className="text-center text-sm font-semibold tabular-nums" />
                            <NumberField.IncrementButton />
                        </NumberField.Group>
                    </NumberField>
                </Widget.Header>
                <Widget.Content className="">
                    <Table variant="secondary">
                        <Table.ScrollContainer>
                            <Table.Content
                                aria-label={t(
                                    'operations.editor.preview.caption',
                                )}
                            >
                                <Table.Header>
                                    <Table.Column isRowHeader>
                                        {t('operations.editor.preview.account')}
                                    </Table.Column>
                                    <Table.Column className="w-28 text-end">
                                        {t('operations.editor.debit')}
                                    </Table.Column>
                                    <Table.Column className="w-28 text-end">
                                        {t('operations.editor.credit')}
                                    </Table.Column>
                                </Table.Header>
                                <Table.Body>
                                    {preview.lines.map((line) => {
                                        const account = line.accountId
                                            ? findLedgerAccount(
                                                  accounts,
                                                  line.accountId,
                                              )
                                            : null;
                                        return (
                                            <Table.Row
                                                id={line.id}
                                                key={line.id}
                                                {...getNodeTestIdProp(
                                                    `operation-editor-preview-line-${line.id}`,
                                                )}
                                            >
                                                <Table.Cell>
                                                    {line.accountId ? (
                                                        <span className="text-foreground">
                                                            <span className="font-semibold tabular-nums">
                                                                {account?.code ??
                                                                    t(
                                                                        'operations.editor.preview.unknownAccount',
                                                                    )}
                                                            </span>
                                                            {account
                                                                ? ` ${account.name}`
                                                                : ''}
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex rounded-lg border border-dashed border-separator px-2 py-0.5 text-muted">
                                                            {line.slotLabelEn ||
                                                                line.slotLabelKa ||
                                                                t(
                                                                    'operations.editor.preview.unnamedSlot',
                                                                )}
                                                        </span>
                                                    )}
                                                    {line.amountSource ===
                                                    'USER_INPUT' ? (
                                                        <span className="ms-2 text-xs text-muted">
                                                            {t(
                                                                'operations.editor.preview.userInputNote',
                                                            )}
                                                        </span>
                                                    ) : null}
                                                </Table.Cell>
                                                <Table.Cell
                                                    className={figureClass(
                                                        line.debitTetri > 0,
                                                        'debit',
                                                    )}
                                                >
                                                    {line.debitTetri > 0
                                                        ? formatGel(
                                                              line.debitTetri,
                                                          )
                                                        : '—'}
                                                </Table.Cell>
                                                <Table.Cell
                                                    className={figureClass(
                                                        line.creditTetri > 0,
                                                        'credit',
                                                    )}
                                                >
                                                    {line.creditTetri > 0
                                                        ? formatGel(
                                                              line.creditTetri,
                                                          )
                                                        : '—'}
                                                </Table.Cell>
                                            </Table.Row>
                                        );
                                    })}
                                    <Table.Row
                                        className="bg-surface-secondary [&_td]:border-b-0"
                                        id="totals"
                                        {...getNodeTestIdProp(
                                            'operation-editor-preview-totals',
                                        )}
                                    >
                                        <Table.Cell>
                                            {t(
                                                'operations.editor.preview.total',
                                            )}
                                        </Table.Cell>
                                        <Table.Cell className="text-end text-sm font-bold tabular-nums text-foreground">
                                            {formatGel(preview.totalDebitTetri)}
                                        </Table.Cell>
                                        <Table.Cell className="text-end text-sm font-bold tabular-nums text-foreground">
                                            {formatGel(
                                                preview.totalCreditTetri,
                                            )}
                                        </Table.Cell>
                                    </Table.Row>
                                </Table.Body>
                            </Table.Content>
                        </Table.ScrollContainer>
                        <Table.Footer
                            className={cn(
                                'flex items-center gap-2 px-4 py-2.5 text-xs font-medium',
                                preview.balanced
                                    ? 'bg-success-soft text-success-soft-foreground'
                                    : 'bg-danger-soft text-danger-soft-foreground',
                            )}
                            {...getNodeTestIdProp(
                                'operation-editor-preview-balance',
                            )}
                        >
                            <Icon
                                aria-hidden
                                className="size-3.5 shrink-0"
                                icon={
                                    preview.balanced
                                        ? 'gravity-ui:check'
                                        : 'gravity-ui:circle-exclamation'
                                }
                            />
                            {t(balance.key, balance.params)}
                        </Table.Footer>
                    </Table>
                </Widget.Content>
                <Widget.Footer>
                    <span
                        className="text-xs text-muted"
                        {...getNodeTestIdProp('operation-editor-preview-rate')}
                    >
                        {vatRateBps > 0
                            ? t('operations.editor.preview.rate', {
                                  rate: bpsToPercentString(vatRateBps),
                              })
                            : t('operations.editor.preview.noRate')}
                    </span>
                </Widget.Footer>
            </Widget>
        </section>
    );
};

/**
 * One money cell's classes.
 *
 * Colour carries which side it is, and weight carries whether it is the side
 * that posts — but neither carries it alone: the column header says debit or
 * credit for a reader who sees no colour, and the em dash says "nothing here"
 * for one who sees no weight.
 */
const figureClass = (isFilled: boolean, tone: 'debit' | 'credit'): string =>
    cn(
        'text-end text-sm tabular-nums',
        isFilled && tone === 'debit' && 'font-semibold text-danger',
        isFilled && tone === 'credit' && 'font-semibold text-success',
        !isFilled && 'text-muted/50',
    );
